import { useState, useEffect, useRef, useCallback } from 'react';

interface IScrollbarDetectionProps {
  stabilityDelay?: 100 | undefined;
  element?: any;
}

interface IScrollInfo {
  hasVerticalScrollbar: boolean;
  hasHorizontalScrollbar: boolean;
  scrollHeight: number;
  clientHeight: number;
  scrollWidth: number;
  clientWidth: number;
}
/**
 * 监听元素是否出现滚动条，主要用于展开收起功能
 * @param props 
 * @returns 
 */
const useScrollbarDetection = (props?: IScrollbarDetectionProps) => {
  const { stabilityDelay = 100, element: propsElement } = props || {};
  const [scrollInfo, setScrollInfo] = useState<IScrollInfo>({
    hasVerticalScrollbar: false,
    hasHorizontalScrollbar: false,
    scrollHeight: 0,
    clientHeight: 0,
    scrollWidth: 0,
    clientWidth: 0
  });
  const [isChecking, setIsChecking] = useState(false);
  const elementRef = useRef<any>(null);
  const observerRef = useRef<any>(null);
  const element = propsElement || elementRef.current;

  const checkScrollbar = useCallback(() => {
    if (!element) return;
    const info = {
      hasVerticalScrollbar: element.scrollHeight > element.clientHeight,
      hasHorizontalScrollbar: element.scrollWidth > element.clientWidth,
      scrollHeight: element.scrollHeight,
      clientHeight: element.clientHeight,
      scrollWidth: element.scrollWidth,
      clientWidth: element.clientWidth
    };
    setScrollInfo(info);
    return info;
  }, [element]);

  const waitForRenderAndCheck = useCallback(() => {
    if (!element) return;
    setIsChecking(true);
    // 立即检查一次
    checkScrollbar();
    // 设置观察器等待渲染完成
    observerRef.current = new MutationObserver(() => {
      // 防抖检查
      setTimeout(() => {
        checkScrollbar();
      }, stabilityDelay);
    });

    observerRef.current.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });

    // 同时使用 ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        checkScrollbar();
      }, stabilityDelay);
    });

    resizeObserver.observe(element);

    return () => {
      observerRef.current?.disconnect();
      resizeObserver.disconnect();
    };
  }, [element, checkScrollbar, stabilityDelay]);

  useEffect(() => () => {
    observerRef.current?.disconnect();
  }, []);

  return {
    elementRef,
    scrollInfo,
    isChecking,
    checkScrollbar,
    waitForRenderAndCheck
  };
};

export default useScrollbarDetection;